<template>
    <div class="min-h-screen bg-gray-100 overflow-x-hidden">
        <AdminNavbar active="dashboard" />

        <div class="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
            <h1 class="text-base sm:text-xl font-bold text-gray-800 mb-3 sm:mb-6">Contas</h1>

            <div v-if="error" class="text-red-600 text-sm mb-4">{{ error }}</div>

            <!-- Summary cards -->
            <div class="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <template v-if="loading">
                    <div v-for="i in 5" :key="i" class="bg-white border border-gray-100 shadow-sm rounded-xl p-4 min-w-0 overflow-hidden" :class="{ 'hidden sm:block': i === 3 }">
                        <div class="skeleton h-3 w-20 rounded mb-2"></div>
                        <div class="skeleton h-7 w-14 rounded"></div>
                    </div>
                </template>
                <template v-else>
                    <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-4 min-w-0 overflow-hidden">
                        <div class="text-xs text-gray-400 mb-1">Total de contas</div>
                        <div class="text-xl sm:text-2xl font-bold text-gray-800 break-words">{{ companies.length }}</div>
                    </div>
                    <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-4 min-w-0 overflow-hidden">
                        <div class="text-xs text-gray-400 mb-1">Ativas</div>
                        <div class="text-xl sm:text-2xl font-bold text-emerald-600 break-words">{{ activeCount }}</div>
                    </div>
                    <div class="hidden sm:block bg-white border border-gray-100 shadow-sm rounded-xl p-4 min-w-0 overflow-hidden">
                        <div class="text-xs text-gray-400 mb-1">Em teste</div>
                        <div class="text-xl sm:text-2xl font-bold text-cyan-600 break-words">{{ trialCount }}</div>
                    </div>
                    <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-4 min-w-0 overflow-hidden">
                        <div class="text-xs text-gray-400 mb-1">Custo IA no mês (USD)</div>
                        <div class="text-xl sm:text-2xl font-bold text-amber-600 break-all">${{ totalAiCost }}</div>
                    </div>
                    <router-link :to="{ name: 'adminMrr', params: { uuid } }" class="block bg-white border border-gray-100 shadow-sm rounded-xl p-4 min-w-0 overflow-hidden hover:border-cyan-200 transition-colors">
                        <div class="text-xs text-gray-400 mb-1">Receita Mensal</div>
                        <div v-if="loadingMrr" class="skeleton h-7 w-20 rounded"></div>
                        <div v-else class="text-xl sm:text-2xl font-bold text-cyan-600 break-words">{{ formatBrl(mrrBrl) }}</div>
                    </router-link>
                </template>
            </div>

            <!-- Search -->
            <input
                v-model="searchInput"
                type="text"
                placeholder="Buscar nome ou email"
                class="input input-bordered w-full min-w-0 text-sm font-bold placeholder:font-bold"
                @keyup.enter="applySearch"
            />
            <div class="mt-2 text-xs text-gray-400">Pressione Enter para buscar.</div>
            <div v-if="activeSearch" class="flex flex-wrap items-center gap-2 mb-6 text-xs">
                <span class="text-gray-500">Filtrado por: <strong class="text-gray-700">"{{ activeSearch }}"</strong></span>
                <button type="button" class="text-cyan-600 hover:text-cyan-700 font-semibold" @click="clearSearch">Limpar filtro ✕</button>
            </div>
            <div v-else class="mb-6"></div>

            <!-- Loading skeleton (shared) -->
            <div v-if="loading" class="space-y-3">
                <div v-for="i in 6" :key="i" class="skeleton h-20 w-full rounded-xl"></div>
            </div>

            <template v-else>
                <div v-if="pagedCompanies.length === 0" class="bg-white border border-gray-100 rounded-xl py-8 text-center text-gray-400 text-sm">
                    Nenhuma conta encontrada.
                </div>

                <!-- Desktop table -->
                <div v-else class="hidden sm:block overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
                <table class="table table-sm w-full min-w-[900px]">
                    <thead>
                        <tr class="text-[11px] uppercase tracking-wide">
                            <th class="text-left !text-black">
                                <button type="button" class="inline-flex items-center gap-1 hover:text-gray-600 transition-colors whitespace-nowrap" @click="toggleSort('created_at')">
                                    Criada em
                                    <span class="text-[10px]" :class="sortField === 'created_at' ? 'text-black' : 'text-gray-300'">{{ sortField === 'created_at' && sortDirection === 'asc' ? '▲' : '▼' }}</span>
                                </button>
                            </th>
                            <th class="text-center !text-black">Status</th>
                            <th class="text-left !text-black">Conta</th>
                            <th class="text-center !text-black">Usuários</th>
                            <th class="text-center !text-black">Pac.</th>
                            <th class="text-center !text-black" title="Custo IA vs Receita (mês)">IA vs Receita</th>
                            <th class="text-center !text-black">Assinatura</th>
                            <th class="text-left !text-black">
                                <button type="button" class="inline-flex items-center gap-1 hover:text-gray-600 transition-colors whitespace-nowrap" @click="toggleSort('last_used_at')">
                                    Último uso
                                    <span class="text-[10px]" :class="sortField === 'last_used_at' ? 'text-black' : 'text-gray-300'">{{ sortField === 'last_used_at' && sortDirection === 'asc' ? '▲' : '▼' }}</span>
                                </button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="c in pagedCompanies"
                            :key="c.id"
                            class="hover:bg-gray-50 transition-colors cursor-pointer"
                            @click="router.push({ name: 'adminCompanyDetail', params: { uuid, id: c.id } })"
                        >
                            <td class="whitespace-nowrap">
                                <div class="text-xs text-gray-600">{{ formatDateTime(c.created_at) }}</div>
                                <div class="text-xs text-gray-400">{{ formatRelative(c.created_at) }}</div>
                            </td>
                            <td class="text-center">
                                <span :class="adminBadgeClass(c.status ? 'success' : 'error')">{{ c.status ? 'Ativa' : 'Inativa' }}</span>
                            </td>
                            <td>
                                <div class="font-medium text-gray-800">{{ c.name }}</div>
                                <div class="text-xs text-gray-400">{{ c.email }}</div>
                            </td>
                            <td class="text-center text-gray-600">{{ c.user_count }}</td>
                            <td class="text-center text-gray-600">{{ c.patient_count }}</td>
                            <td class="text-center whitespace-nowrap">
                                <div class="text-amber-600">${{ (Number(c.ai_cost_usd_month) || 0).toFixed(4) }}</div>
                                <div
                                    class="text-xs font-semibold"
                                    :class="(c.ai_cost_ratio_month || 0) > 80 ? 'text-red-600' : (c.ai_cost_ratio_month || 0) > 40 ? 'text-orange-500' : 'text-emerald-600'"
                                >{{ Number(c.ai_cost_ratio_month) || 0 }}% da receita</div>
                            </td>
                            <td class="text-center whitespace-nowrap">
                                <span :class="adminBadgeClass(subscriptionBadgeTone(c.subscription_status))">{{ planLabel(c.plan) }}</span>
                                <div v-if="c.subscription_status === 'trial'" class="text-xs text-gray-400 mt-0.5">{{ formatDaysUntil(c.trial_ends_at) }}</div>
                                <template v-else-if="c.subscription_status === 'expired_trial'">
                                    <div class="text-xs text-gray-500 mt-0.5">{{ formatDateTime(c.trial_ends_at || c.created_at) }}</div>
                                    <div class="text-xs text-gray-400">{{ formatRelative(c.trial_ends_at || c.created_at) }}</div>
                                </template>
                            </td>
                            <td class="whitespace-nowrap">
                                <template v-if="c.last_used_at">
                                    <div class="text-xs text-gray-600">{{ formatDateTime(c.last_used_at) }}</div>
                                    <div class="text-xs text-gray-400">{{ formatRelative(c.last_used_at) }}</div>
                                </template>
                                <span v-else class="text-gray-300 text-xs">—</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
                </div>

                <!-- Mobile cards -->
                <div class="sm:hidden space-y-3">
                    <div
                        v-for="c in pagedCompanies"
                        :key="c.id"
                        class="bg-white border border-gray-100 rounded-xl shadow-sm p-4 min-w-0 overflow-hidden active:bg-gray-50"
                        @click="router.push({ name: 'adminCompanyDetail', params: { uuid, id: c.id } })"
                    >
                        <div class="flex flex-wrap items-center gap-2 mb-3">
                            <span :class="adminBadgeClass(c.status ? 'success' : 'error')">{{ c.status ? 'Ativa' : 'Inativa' }}</span>
                            <span :class="adminBadgeClass(subscriptionBadgeTone(c.subscription_status))">{{ planLabel(c.plan) }}</span>
                        </div>

                        <div class="min-w-0 mb-4">
                            <div class="text-2xl font-semibold leading-tight text-gray-800 truncate">{{ c.name }}</div>
                            <div class="text-sm text-gray-400 truncate mt-1">{{ c.email }}</div>
                            <div v-if="c.subscription_status === 'trial'" class="text-xs text-cyan-600 mt-2">
                                {{ formatDaysUntil(c.trial_ends_at) }}
                            </div>
                            <div v-else-if="c.subscription_status === 'expired_trial'" class="text-xs text-gray-400 mt-2">
                                Encerrado {{ formatRelative(c.trial_ends_at || c.created_at) }}
                            </div>
                        </div>

                        <div class="grid grid-cols-2 gap-3 mb-4">
                            <div class="rounded-lg bg-gray-50 px-3 py-2">
                                <div class="text-[11px] uppercase tracking-wide text-gray-400">Usuários</div>
                                <div class="text-lg font-semibold text-gray-700 mt-1">{{ c.user_count }}</div>
                            </div>
                            <div class="rounded-lg bg-gray-50 px-3 py-2">
                                <div class="text-[11px] uppercase tracking-wide text-gray-400">Pacientes</div>
                                <div class="text-lg font-semibold text-gray-700 mt-1">{{ c.patient_count }}</div>
                            </div>
                            <div class="col-span-2 rounded-lg bg-amber-50 px-3 py-2">
                                <div class="text-[11px] uppercase tracking-wide text-amber-500">IA vs Receita (mês)</div>
                                <div class="flex items-baseline gap-2 mt-1">
                                    <span class="text-lg font-semibold text-amber-700">${{ (Number(c.ai_cost_usd_month) || 0).toFixed(2) }}</span>
                                    <span
                                        class="text-xs font-semibold"
                                        :class="(c.ai_cost_ratio_month || 0) > 80 ? 'text-red-600' : (c.ai_cost_ratio_month || 0) > 40 ? 'text-orange-500' : 'text-emerald-600'"
                                    >{{ Number(c.ai_cost_ratio_month) || 0 }}%</span>
                                </div>
                            </div>
                        </div>

                        <div class="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100">
                            <div class="min-w-0">
                                <div class="text-[11px] uppercase tracking-wide text-gray-400">Criada</div>
                                <div class="text-sm text-gray-600 mt-1">{{ formatRelative(c.created_at) }}</div>
                            </div>
                            <div class="min-w-0">
                                <div class="text-[11px] uppercase tracking-wide text-gray-400">Último uso</div>
                                <div v-if="c.last_used_at" class="text-sm text-gray-600 mt-1">{{ formatRelative(c.last_used_at) }}</div>
                                <div v-else class="text-sm text-gray-400 mt-1">Nunca usado</div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Pagination -->
            <div v-if="!loading && totalPages > 1" class="flex flex-col items-center gap-2 mt-4">
                <div class="join">
                    <button type="button" class="join-item btn btn-sm !text-sm !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight" :disabled="currentPage === 1" @click="currentPage--">«</button>
                    <button type="button" class="join-item btn btn-sm !text-sm !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight">{{ currentPage }}</button>
                    <button type="button" class="join-item btn btn-sm !text-sm !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight" :disabled="currentPage === totalPages" @click="currentPage++">»</button>
                </div>
                <span class="text-xs text-gray-400">Página {{ currentPage }} de {{ totalPages }} ({{ sortedCompanies.length }} contas)</span>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AdminCompanyService from '@/services/admin/AdminCompanyService';
import AdminStripeService from '@/services/admin/AdminStripeService';
import { adminBadgeClass } from '@/constants/adminBadge';
import { PLANS } from '@/constants/plans';
import AdminNavbar from '@/components/admin/AdminNavbar.vue';

export default {
    name: 'AdminDashboard',
    components: { AdminNavbar },
    setup() {
        const route = useRoute();
        const router = useRouter();
        const uuid = computed(() => route.params.uuid);
        const companies = ref([]);
        const loading = ref(true);
        const loadingMrr = ref(true);
        const mrrBrl = ref(0);
        const error = ref('');
        const searchInput = ref('');
        const activeSearch = ref('');
        const sortField = ref('created_at');
        const sortDirection = ref('desc');
        const currentPage = ref(1);
        const pageSize = 15;

        const activeCount = computed(() => companies.value.filter(c => c.status).length);
        const trialCount = computed(() => companies.value.filter(c => c.subscription_status === 'trial').length);
        const totalAiCost = computed(() => companies.value.reduce((s, c) => s + Number(c.ai_cost_usd_month || 0), 0).toFixed(4));

        const sortedCompanies = computed(() => {
            const list = [...companies.value];
            const dir = sortDirection.value === 'asc' ? 1 : -1;
            const field = sortField.value;
            return list.sort((a, b) => {
                const aTime = a[field] ? new Date(a[field]).getTime() : 0;
                const bTime = b[field] ? new Date(b[field]).getTime() : 0;
                return (aTime - bTime) * dir;
            });
        });

        function toggleSort(field) {
            if (sortField.value === field) {
                sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
            } else {
                sortField.value = field;
                sortDirection.value = 'desc';
            }
            currentPage.value = 1;
        }

        const totalPages = computed(() => Math.max(1, Math.ceil(sortedCompanies.value.length / pageSize)));
        const pagedCompanies = computed(() => {
            const start = (currentPage.value - 1) * pageSize;
            return sortedCompanies.value.slice(start, start + pageSize);
        });

        async function loadCompanies(search) {
            loading.value = true;
            error.value = '';
            try {
                const { data } = await AdminCompanyService.list(search);
                companies.value = data.data;
            } catch {
                error.value = 'Erro ao carregar contas';
            } finally {
                loading.value = false;
            }
        }

        async function loadMrr() {
            loadingMrr.value = true;
            try {
                const { data } = await AdminStripeService.getMrr();
                mrrBrl.value = data.data.mrr_brl || 0;
            } catch {
                mrrBrl.value = 0;
            } finally {
                loadingMrr.value = false;
            }
        }

        function applySearch() {
            currentPage.value = 1;
            const term = searchInput.value.trim();
            activeSearch.value = term;
            loadCompanies(term);
        }

        function clearSearch() {
            searchInput.value = '';
            activeSearch.value = '';
            currentPage.value = 1;
            loadCompanies();
        }

        function formatBrl(value) {
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
        }

        function planLabel(planId) {
            const found = PLANS.find((plan) => plan.id === (planId || 'basico'));
            return found ? found.name : 'Plano Básico';
        }

        function subscriptionBadgeTone(status) {
            if (status === 'past_due') return 'warning';
            if (status === 'expired_trial') return 'error';
            if (status === 'trial') return 'info';
            return 'success';
        }

        function formatDateTime(dateStr) {
            if (!dateStr) return '—';
            return new Date(dateStr).toLocaleString('pt-BR', {
                day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
            });
        }

        function formatDaysUntil(dateStr) {
            if (!dateStr) return '';
            const diffMs = new Date(dateStr).getTime() - Date.now();
            const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
            if (days <= 0) return 'acaba hoje';
            return `acaba em ${days} dia${days !== 1 ? 's' : ''}`;
        }

        function formatRelative(dateStr) {
            if (!dateStr) return '';
            const diffSec = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
            if (diffSec < 60) return 'agora mesmo';
            const diffMin = Math.floor(diffSec / 60);
            if (diffMin < 60) return `há ${diffMin} min`;
            const diffHour = Math.floor(diffMin / 60);
            if (diffHour < 24) return `há ${diffHour}h`;
            const diffDay = Math.floor(diffHour / 24);
            if (diffDay < 30) return `há ${diffDay}d`;
            const diffMonth = Math.floor(diffDay / 30);
            if (diffMonth < 12) return `há ${diffMonth} ${diffMonth === 1 ? 'mês' : 'meses'}`;
            const diffYear = Math.floor(diffMonth / 12);
            return `há ${diffYear} ${diffYear === 1 ? 'ano' : 'anos'}`;
        }

        onMounted(() => {
            loadCompanies();
            loadMrr();
        });

        return {
            uuid, router, companies, sortedCompanies, pagedCompanies, sortField, sortDirection, toggleSort,
            currentPage, totalPages, loading, loadingMrr, mrrBrl, error, searchInput, activeSearch,
            activeCount, trialCount, totalAiCost, applySearch, clearSearch, formatBrl, formatDateTime, formatRelative,
            formatDaysUntil, adminBadgeClass, planLabel, subscriptionBadgeTone
        };
    }
};
</script>
